import { Braces, FileCode2 } from 'lucide-react'
import { useState } from 'react'
import type { RunFileChanges } from './runFileChanges'
import { fileLabel } from './SessionChangesPanel'

export function RunFilesSummary({
  run,
  title = 'Changes',
  initialRows = 5,
  folder,
  sourceId,
  onOpenDocument,
}: {
  run: RunFileChanges
  title?: string
  initialRows?: number
  folder: string | null
  sourceId?: string
  onOpenDocument?: (id: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const limit = Math.max(1, Math.min(initialRows, 20))
  const shown = expanded ? run.files : run.files.slice(0, limit)
  const branches = new Set(run.files.map((file) => file.writtenOn?.branch))
  const branch = branches.size === 1 ? [...branches][0] : undefined
  return (
    <div
      className="min-w-0 overflow-hidden rounded-xl border border-border/70 bg-card"
      title="Files changed in this run, measured from Git when available"
    >
      <div className="min-w-0 px-4 py-3">
        <div className="text-sm font-medium">
          {title} {run.files.length}
        </div>
        {branch ? (
          <div
            className="min-w-0 truncate text-xs text-muted-foreground"
            title={branch}
          >
            {branch}
          </div>
        ) : null}
      </div>
      <div className="border-border/60 border-t">
        {shown.map((file) => {
          const named = fileLabel(file, folder, sourceId)
          const Icon = file.path.endsWith('.json') ? Braces : FileCode2
          return (
            <div
              key={file.path}
              className="group flex min-h-10 min-w-0 items-center gap-3 border-border/40 border-b px-4 py-2 text-sm last:border-b-0"
              title={file.path}
            >
              <Icon className="size-4 shrink-0 text-muted-foreground/70" />
              {/* Focusing an openable row shows the whole path; hover does
                  through the title on every row. */}
              <span className="flex min-w-0 flex-1 flex-col">
                {named.codeId && onOpenDocument ? (
                  <button
                    type="button"
                    className="min-w-0 truncate text-left text-foreground hover:underline focus-visible:underline focus-visible:outline-none"
                    onClick={() => onOpenDocument(named.codeId as string)}
                  >
                    {named.label}
                  </button>
                ) : (
                  <span className="min-w-0 truncate text-foreground">
                    {named.label}
                  </span>
                )}
                <span className="hidden break-all text-xs text-muted-foreground group-focus-within:block">
                  {file.path}
                </span>
              </span>
              <span
                className="ml-auto flex shrink-0 gap-1.5 tabular-nums text-xs"
                title="Net Git lines when available; otherwise estimated from tool calls"
              >
                <span className="text-green-600">+{file.insertions}</span>
                <span className="text-red-600">−{file.deletions}</span>
              </span>
            </div>
          )
        })}
      </div>
      {run.files.length > limit ? (
        <button
          type="button"
          className="w-full border-border/60 border-t px-4 py-3 text-left text-sm text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show fewer' : `${run.files.length - limit} more`}
        </button>
      ) : null}
    </div>
  )
}
